import React, { useState } from 'react';
import { History, Loader2, Plus, X, Receipt, Paperclip } from 'lucide-react';
import { LavoroAPosterioriService } from '../services/apiService';
import { useToast } from './ToastContext';
import { parseDecimal } from '../utils/utils';
import AutoAziendaleSelector from './AutoAziendaleSelector';
import { useAvvisoCompresenze } from './AvvisoCompresenze';

/**
 * Inserimento a posteriori di un incarico o di un turno già svolto (es. durante un fermo del
 * gestionale): il titolare/responsabile mette orari effettivi, report, km, ore extra e spese,
 * e il report nasce già approvato con le regole dell'approvazione (buoni pasto compresi).
 */
interface Props {
    mode: 'incarico' | 'turno';
    praticaId?: number;
    servizioId?: number;
    collaboratori: { id: number; nome: string }[];
    // Soggetti del mandato, solo per i mandati investigativi: vuoto = campo nascosto.
    soggetti?: string[];
    onClose: () => void;
    onSaved: () => void;
}

interface SpesaRow { tipo: string; importo: string; note: string; anticipatoAzienda: boolean; file: File | null; }

const CATEGORIE = ['Pasti', 'Trasporti', 'Carburante', 'Alloggio', 'Altro'];

const isoGiorno = (offset: number) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const LavoroAPosterioriModal: React.FC<Props> = ({ mode, praticaId, servizioId, collaboratori, soggetti = [], onClose, onSaved }) => {
    const { addToast } = useToast();
    // Turno: avviso se sul servizio c'è già un altro collaboratore negli stessi orari (non blocca).
    const avvisoCompresenze = useAvvisoCompresenze();
    const [collaboratoreId, setCollaboratoreId] = useState<string>(collaboratori.length === 1 ? String(collaboratori[0].id) : '');
    const [data, setData] = useState(isoGiorno(-1));
    const [oraInizio, setOraInizio] = useState('');
    const [oraFine, setOraFine] = useState('');
    const [soggettoNome, setSoggettoNome] = useState(soggetti.length === 1 ? soggetti[0] : '');
    const [autoAziendale, setAutoAziendale] = useState(false);
    const [oreFatturabili, setOreFatturabili] = useState('');
    const [relazione, setRelazione] = useState('');
    const [km, setKm] = useState('');
    const [oreExtra, setOreExtra] = useState('');
    const [buonoPasto, setBuonoPasto] = useState(true);
    const [spese, setSpese] = useState<SpesaRow[]>([]);
    const [saving, setSaving] = useState(false);

    const isIncarico = mode === 'incarico';
    const fineGiornoDopo = !!oraInizio && !!oraFine && oraFine <= oraInizio;

    const updateSpesa = (i: number, field: keyof SpesaRow, value: any) =>
        setSpese(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!collaboratoreId) { addToast('Seleziona il collaboratore', 'error'); return; }
        if (!data || !oraInizio || !oraFine) { addToast('Inserisci data, ora di inizio e ora di fine', 'error'); return; }
        if (oraInizio === oraFine) { addToast('Ora di inizio e ora di fine coincidono', 'error'); return; }
        if (isIncarico && soggetti.length > 1 && !soggettoNome) { addToast('Seleziona il soggetto', 'error'); return; }
        if (relazione.trim().length < 10) { addToast('Scrivi il report (almeno 10 caratteri)', 'error'); return; }
        for (let i = 0; i < spese.length; i++) {
            if (!spese[i].tipo) { addToast(`Spesa #${i + 1}: seleziona la categoria`, 'error'); return; }
            if (parseDecimal(spese[i].importo) <= 0) { addToast(`Spesa #${i + 1}: importo non valido`, 'error'); return; }
        }
        if (!isIncarico && servizioId) {
            const procedi = await avvisoCompresenze.conferma(servizioId, Number(collaboratoreId),
                [{ dataTurno: data, oraInizio, oraFine, notturno: oraFine < oraInizio }]);
            if (!procedi) return;
        }
        setSaving(true);
        try {
            const payload: any = {
                collaboratoreId: Number(collaboratoreId),
                data, oraInizio, oraFine,
                relazione,
                km: km ? parseDecimal(km) : null,
                oreExtra: oreExtra ? parseDecimal(oreExtra) : null,
                applicaBuonoPasto: buonoPasto,
                spese: spese.map(s => ({ tipo: s.tipo, importo: parseDecimal(s.importo), note: s.note, anticipatoAzienda: s.anticipatoAzienda })),
            };
            if (isIncarico) {
                Object.assign(payload, {
                    praticaId, soggettoNome: soggettoNome || null, autoAziendale,
                    oreFatturabili: oreFatturabili ? parseDecimal(oreFatturabili) : null,
                });
            }
            const fd = new FormData();
            fd.append('data', JSON.stringify(payload));
            spese.forEach((s, idx) => { if (s.file) fd.append(`file_${idx}`, s.file); });
            if (isIncarico) await LavoroAPosterioriService.creaIncarico(fd);
            else await LavoroAPosterioriService.creaTurno(servizioId!, fd);
            addToast(isIncarico ? 'Incarico inserito e approvato' : 'Turno inserito e approvato', 'success');
            onSaved();
            onClose();
        } catch (err: any) {
            addToast(err?.response?.data?.message || 'Errore durante l\'inserimento', 'error');
        } finally {
            setSaving(false);
        }
    };

    const input = 'w-full p-2.5 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm';
    const label = 'block text-xs font-bold text-slate-500 uppercase mb-1';

    return (
        <>
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <History size={20} className="text-indigo-600" />
                        {isIncarico ? 'Inserisci incarico già svolto' : 'Inserisci turno già svolto'}
                    </h3>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                </div>

                <div className="p-6 space-y-6">
                    <p className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        Per il lavoro svolto senza l'app (ad esempio durante un fermo). {isIncarico ? 'L\'incarico' : 'Il turno'} viene
                        registrato come concluso e il report come <b>già approvato</b>: spese, km, ore e buono pasto seguono le regole
                        dell'approvazione. Il collaboratore non riceve notifiche.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                            <label className={label}>Collaboratore</label>
                            <select className={input} value={collaboratoreId} onChange={e => setCollaboratoreId(e.target.value)}>
                                <option value="">-- Seleziona --</option>
                                {collaboratori.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className={label}>Data</label>
                            <input type="date" className={input} value={data} max={isoGiorno(0)} onChange={e => setData(e.target.value)} />
                        </div>
                        <div>
                            <label className={label}>Ora inizio effettiva</label>
                            <input type="time" className={input} value={oraInizio} onChange={e => setOraInizio(e.target.value)} />
                        </div>
                        <div>
                            <label className={label}>Ora fine effettiva</label>
                            <input type="time" className={input} value={oraFine} onChange={e => setOraFine(e.target.value)} />
                        </div>
                        <div className="md:col-span-2 flex items-end text-xs text-slate-500 pb-3">
                            {fineGiornoDopo && 'La fine cade il giorno dopo (oltre la mezzanotte).'}
                        </div>
                    </div>

                    {isIncarico && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {soggetti.length > 0 && (
                                <div className="md:col-span-2">
                                    <label className={label}>Soggetto</label>
                                    <select className={input} value={soggettoNome} onChange={e => setSoggettoNome(e.target.value)}>
                                        {soggetti.length > 1 && <option value="">-- Seleziona --</option>}
                                        {soggetti.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className={label}>Ore da fatturare al cliente</label>
                                <input className={input} inputMode="decimal" placeholder="Vuoto = orari dell'indagine"
                                       value={oreFatturabili} onChange={e => setOreFatturabili(e.target.value)} />
                            </div>
                            <div>
                                <label className={label}>Veicolo</label>
                                <AutoAziendaleSelector value={autoAziendale} onChange={setAutoAziendale} />
                                {autoAziendale && <p className="text-[11px] text-slate-500 mt-1">Km non rimborsati al collaboratore</p>}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className={label}>Report</label>
                        <textarea className={`${input} min-h-[110px]`} value={relazione} onChange={e => setRelazione(e.target.value)}
                                  placeholder="Cosa è stato fatto" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={label}>Km</label>
                            <input className={input} inputMode="decimal" value={km} onChange={e => setKm(e.target.value)} placeholder="0" />
                        </div>
                        <div>
                            <label className={label}>Ore extra</label>
                            <input className={input} inputMode="decimal" value={oreExtra} onChange={e => setOreExtra(e.target.value)} placeholder="0" />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-700 md:pt-6">
                            <input type="checkbox" checked={buonoPasto} onChange={e => setBuonoPasto(e.target.checked)} />
                            Calcola buono pasto
                        </label>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className={label + ' mb-0 flex items-center gap-1'}><Receipt size={14} /> Spese</span>
                            <button type="button" onClick={() => setSpese(p => [...p, { tipo: '', importo: '', note: '', anticipatoAzienda: false, file: null }])}
                                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                                <Plus size={16} /> Aggiungi spesa
                            </button>
                        </div>
                        {spese.length === 0 && <p className="text-sm text-slate-400">Nessuna spesa.</p>}
                        <div className="space-y-3">
                            {spese.map((s, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-lg p-3">
                                    <select className={`${input} col-span-3`} value={s.tipo} onChange={e => updateSpesa(i, 'tipo', e.target.value)}>
                                        <option value="">Categoria</option>
                                        {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <input className={`${input} col-span-2`} inputMode="decimal" placeholder="€" value={s.importo}
                                           onChange={e => updateSpesa(i, 'importo', e.target.value)} />
                                    <input className={`${input} col-span-2`} placeholder="Note" value={s.note}
                                           onChange={e => updateSpesa(i, 'note', e.target.value)} />
                                    <label className="col-span-2 flex items-center gap-1 text-xs text-slate-600">
                                        <input type="checkbox" checked={s.anticipatoAzienda} onChange={e => updateSpesa(i, 'anticipatoAzienda', e.target.checked)} />
                                        Carta az.
                                    </label>
                                    {/* Pulsante al posto dell'input nativo, che nella colonna stretta veniva tagliato. */}
                                    <label className="col-span-2 flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 cursor-pointer min-w-0"
                                           title={s.file ? s.file.name : 'Allega scontrino'}>
                                        <Paperclip size={14} className="shrink-0" />
                                        <span className="truncate">{s.file ? s.file.name : 'Scontrino'}</span>
                                        <input type="file" accept="image/*,application/pdf" className="hidden"
                                               onChange={e => updateSpesa(i, 'file', e.target.files?.[0] || null)} />
                                    </label>
                                    <button type="button" onClick={() => setSpese(p => p.filter((_, idx) => idx !== i))}
                                            className="col-span-1 text-slate-400 hover:text-red-600 justify-self-end"><X size={18} /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 font-semibold">Annulla</button>
                    <button type="submit" disabled={saving}
                            className="px-5 py-2 rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
                        {saving && <Loader2 size={16} className="animate-spin" />} Inserisci e approva
                    </button>
                </div>
            </form>
        </div>
        {avvisoCompresenze.modale}
        </>
    );
};

export default LavoroAPosterioriModal;
